import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Stack,
  IconButton,
  FormControlLabel,
  Switch,
  Chip,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Note, User } from '../../types';
import { NoteService } from '../../services/noteService';
import MentionInput from '../common/MentionInput';
import { SelectedMention } from '../../types/mention';
import { mentionParser } from '../../utils/mention-parser';

interface NoteEditorProps {
  personId: string;
  personName: string;
  currentUser: User;
  existingNote: Note | null;
  onSave: (note: Note) => void;
  onCancel?: () => void;
}

const NOTE_COLORS = [
  { value: '#FFFBF0', label: 'Default' },
  { value: '#FFF4E6', label: 'Cream' },
  { value: '#FFE6E6', label: 'Pink' },
  { value: '#E6F7FF', label: 'Blue' },
  { value: '#E6FFE6', label: 'Green' },
  { value: '#FFF0F5', label: 'Lavender' },
  { value: '#F0F0F0', label: 'Gray' },
];

const NoteEditor: React.FC<NoteEditorProps> = ({
  personId,
  personName,
  currentUser,
  existingNote,
  onSave,
  onCancel,
}) => {
  const [content, setContent] = useState(existingNote?.content || '');
  const [isPrivate, setIsPrivate] = useState(existingNote?.isPrivate ?? true);
  const [tags, setTags] = useState<string[]>(existingNote?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [color, setColor] = useState(existingNote?.color || NOTE_COLORS[0].value);
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle mention change
  const handleMentionChange = useCallback((newValue: string, newMentions: SelectedMention[]) => {
    setContent(newValue);
    setSelectedMentions(newMentions);
  }, []);

  // Handle add tag
  const handleAddTag = useCallback(() => {
    const trimmedTag = tagInput.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags(prev => [...prev, trimmedTag]);
      setTagInput('');
    }
  }, [tagInput, tags]);

  // Handle remove tag
  const handleRemoveTag = useCallback((tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  }, []);

  // Handle tag input key press
  const handleTagInputKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAddTag();
      }
    },
    [handleAddTag]
  );

  // Handle save
  const handleSave = useCallback(async () => {
    if (!content.trim()) {
      setError('Please enter some content for the note');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Parse mentions from content and selected mentions
      const parsedMentions = mentionParser.parse(content, selectedMentions);

      let savedNote: Note;

      if (existingNote?.id) {
        // Update existing note
        await NoteService.updateNote(
          existingNote.id,
          {
            content: content.trim(),
            isPrivate,
            tags: tags.length > 0 ? tags : undefined,
            color,
            mentions: parsedMentions,
          },
          currentUser.uid,
          currentUser.displayName || 'Unknown',
          currentUser.role
        );

        // Refetch the updated note
        const updatedNote = await NoteService.getNoteById(existingNote.id, currentUser.uid);
        if (!updatedNote) {
          throw new Error('Failed to retrieve updated note');
        }
        savedNote = updatedNote;
      } else {
        // Create new note
        savedNote = await NoteService.createNote(
          {
            personId,
            content: content.trim(),
            isPrivate,
            tags: tags.length > 0 ? tags : undefined,
            color,
            mentions: parsedMentions,
          },
          currentUser.uid,
          currentUser.displayName || 'Unknown',
          currentUser.role
        );
      }

      // Call completion handler
      onSave(savedNote);

      // Reset form
      setContent('');
      setIsPrivate(true);
      setTags([]);
      setTagInput('');
      setColor(NOTE_COLORS[0].value);
      setSelectedMentions([]);
    } catch (err) {
      console.error('Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setIsSaving(false);
    }
  }, [content, selectedMentions, isPrivate, tags, color, personId, personName, currentUser, existingNote, onSave]);

  return (
    <Box>
      <DialogTitle>
        {existingNote ? 'Edit Note' : 'Create Note'}
        {onCancel && (
          <IconButton
            aria-label="close"
            onClick={onCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Error message */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Info message */}
          <Alert severity="info">
            {isPrivate
              ? `Create a private note about ${personName}. Only you can see private notes.`
              : `Create a public note about ${personName}. Public notes are visible to all users.`}
          </Alert>

          {/* Private/Public toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={!isPrivate}
                onChange={(e) => setIsPrivate(!e.target.checked)}
                disabled={isSaving}
              />
            }
            label={isPrivate ? 'Private Note' : 'Public Note'}
          />

          {/* Content */}
          <MentionInput
            label="Note Content"
            value={content}
            onChange={handleMentionChange}
            contentType="note"
            targetId={personId}
            fullWidth
            required
            rows={10}
            disabled={isSaving}
            helperText="Enter your note here (@ to mention someone)"
            placeholder="Type your note..."
            autoFocus
          />

          {/* Tags */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Tags (Optional)
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField
                size="small"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                placeholder="Add a tag..."
                disabled={isSaving}
                fullWidth
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddTag}
                disabled={!tagInput.trim() || isSaving}
              >
                Add
              </Button>
            </Stack>

            {/* Tag list */}
            {tags.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={`#${tag}`}
                      size="small"
                      onDelete={() => handleRemoveTag(tag)}
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              </Paper>
            )}
          </Box>

          {/* Color picker */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Note Color
            </Typography>
            <ToggleButtonGroup
              value={color}
              exclusive
              onChange={(e, newColor) => newColor && setColor(newColor)}
              size="small"
              disabled={isSaving}
            >
              {NOTE_COLORS.map((noteColor) => (
                <ToggleButton
                  key={noteColor.value}
                  value={noteColor.value}
                  sx={{
                    backgroundColor: noteColor.value,
                    '&.Mui-selected': {
                      backgroundColor: noteColor.value,
                      border: '2px solid',
                      borderColor: 'primary.main',
                    },
                    '&:hover': {
                      backgroundColor: noteColor.value,
                      opacity: 0.8,
                    },
                    width: 40,
                    height: 40,
                  }}
                  aria-label={noteColor.label}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!content.trim() || isSaving}
        >
          {isSaving ? 'Saving...' : existingNote ? 'Update Note' : 'Create Note'}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default NoteEditor;
